import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function Login() {
  const { user, setUser, googleError, setSignedOut } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/entries" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      nav("/entries");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen lg:grid-cols-2 overflow-hidden">
      <div className="relative hidden lg:flex min-h-screen flex-col justify-between overflow-hidden bg-[#1F4266] p-14 text-white">
        <img src="/flower-logo.png" alt="" className="absolute -right-40 -bottom-40 w-[720px] opacity-10 invert" />
        <div className="flex items-center gap-3">
          <div className="grid h-11 w-11 place-items-center rounded-xl bg-white">
            <img src="/flower-logo.png" alt="" className="h-8 w-8" />
          </div>
          <span className="font-display text-xl font-bold">Assessment Reports</span>
        </div>
        <div className="max-w-md fade-up">
          <h1 className="text-5xl font-extrabold leading-tight tracking-tight">Body. Mind. Spirit. Self. Others.</h1>
          <p className="mt-5 text-white/70 leading-relaxed">
            Every assessment from Go High Level becomes a personal flower heat map — and every group, a shared picture of growth.
          </p>
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-white/50">Admin access only</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up-2" data-testid="login-form">
          <div>
            <img src="/flower-logo.png" alt="" className="h-12 w-12 lg:hidden mb-6" />
            <h2 className="text-3xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-2 text-sm text-slate-500">Use your administrator account.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="login-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-600">{error}</p>}
          <Button data-testid="login-submit-button" disabled={busy} className="w-full rounded-full bg-[#1F4266] hover:bg-[#122A43] h-11">
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
          </Button>
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-slate-400">
            <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
          </div>
          <Link
            to="/sign-in"
            onClick={() => setSignedOut(false)}
            data-testid="login-google-button"
            className="flex h-11 w-full items-center justify-center gap-2 rounded-full border border-slate-300 bg-white text-sm font-medium text-slate-800 transition-colors hover:bg-slate-50"
          >
            <img src="/google-g.ico" alt="" className="h-4 w-4" /> Sign in with Google
          </Link>
          {googleError && <p data-testid="login-google-error" className="text-sm text-red-600">{googleError}</p>}
        </form>
      </div>
    </div>
  );
}
